/**
 * LocationPickerPage — full-screen Google Map for picking a location.
 *
 * URL params (input):
 *   - lat, lng: initial pin position (optional, defaults to GPS or Dubai)
 *   - returnTo: where to navigate back ('/shops' or '/scan')
 *   - shopId: shop ID (for ShopsPage location editing)
 *   - sectionName, sectionId: (for ScanCapturePage)
 *
 * On confirm, navigates back with ?lat=...&lng=... appended so the
 * calling page can read the chosen coordinates.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { loadGoogleMaps } from '../../utils/loadGoogleMaps'
import './LocationPickerPage.css'

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''

export default function LocationPickerPage() {
  const navigate = useNavigate()
  const { t } = useTranslation('shops')
  const [searchParams] = useSearchParams()
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<google.maps.Map | null>(null)
  const markerRef = useRef<google.maps.Marker | null>(null)
  const [selectedPos, setSelectedPos] = useState<{ lat: number; lng: number } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mapType, setMapType] = useState<'roadmap' | 'satellite'>('roadmap')
  // Read params
  const returnTo = searchParams.get('returnTo') || '/shops'
  const initialLat = searchParams.get('lat') ? parseFloat(searchParams.get('lat')!) : null
  const initialLng = searchParams.get('lng') ? parseFloat(searchParams.get('lng')!) : null
  const shopId = searchParams.get('shopId') || ''
  const sectionName = searchParams.get('sectionName') || ''
  const sectionId = searchParams.get('sectionId') || ''
  const fromSetup = searchParams.get('fromSetup') || ''
  const fromConfirm = searchParams.get('fromConfirm') || ''

  useEffect(() => {
    if (!mapRef.current) return
    if (!GOOGLE_MAPS_API_KEY) {
      setError(t('errors.mapsApiKeyNotConfigured'))
      setLoading(false)
      return
    }
    let cancelled = false

    const initMap = async () => {
      try {
        await loadGoogleMaps(GOOGLE_MAPS_API_KEY)
        if (cancelled || !mapRef.current) return

        if (!mapRef.current) return

        // Determine initial center
        let center: google.maps.LatLngLiteral = { lat: 25.0762, lng: 55.1329 } // Dubai default
        let zoom = 12

        if (initialLat !== null && initialLng !== null && !isNaN(initialLat) && !isNaN(initialLng)) {
          center = { lat: initialLat, lng: initialLng }
          zoom = 16
        }

        const map = new google.maps.Map(mapRef.current, {
          center,
          zoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          gestureHandling: 'greedy',
        })
        mapInstanceRef.current = map

        // Create draggable marker
        const marker = new google.maps.Marker({
          map,
          draggable: true,
          position: center,
          title: t('dragToSetLocation'),
          animation: google.maps.Animation.DROP,
        })
        markerRef.current = marker

        // Set initial position
        if (initialLat !== null && initialLng !== null && !isNaN(initialLat) && !isNaN(initialLng)) {
          setSelectedPos({ lat: initialLat, lng: initialLng })
        } else {
          setSelectedPos({ lat: center.lat, lng: center.lng })
        }

        // Tap map → move marker
        map.addListener('click', (e: google.maps.MapMouseEvent) => {
          if (e.latLng) {
            const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() }
            marker.setPosition(pos)
            setSelectedPos(pos)
          }
        })

        // Drag marker → update position
        marker.addListener('dragend', () => {
          const pos = marker.getPosition()
          if (pos) {
            setSelectedPos({ lat: pos.lat(), lng: pos.lng() })
          }
        })

        if (cancelled) return
        setLoading(false)

        // If no initial coords, try GPS
        if (initialLat === null || initialLng === null) {
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const gpsPos = {
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                }
                map.setCenter(gpsPos)
                map.setZoom(16)
                marker.setPosition(gpsPos)
                setSelectedPos(gpsPos)
              },
              () => {
                // GPS failed, stay on default center
              },
              { enableHighAccuracy: true, timeout: 10000 }
            )
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[LocationPicker] Failed to load map:', err)
          setError(t('errors.failedToLoadMaps'))
          setLoading(false)
        }
      }
    }

    initMap()
    return () => { cancelled = true }
  }, [])

  const handleConfirm = () => {
    if (!selectedPos) return

    // Build return URL with location params
    const params = new URLSearchParams()
    params.set('pickedLat', selectedPos.lat.toFixed(6))
    params.set('pickedLng', selectedPos.lng.toFixed(6))

    if (returnTo === '/scan') {
      if (shopId) params.set('shopId', shopId)
      if (sectionName) params.set('sectionName', sectionName)
      if (sectionId) params.set('sectionId', sectionId)
      if (fromSetup) params.set('fromSetup', fromSetup)
      if (fromConfirm) params.set('fromConfirm', fromConfirm)
      navigate(`/scan?${params.toString()}`)
    } else {
      // Return to the originating page (shops list or shop detail)
      if (shopId) params.set('editLocationShopId', shopId)
      const forNewShop = searchParams.get('forNewShop')
      if (forNewShop) params.set('forNewShop', forNewShop)
      navigate(`${returnTo}?${params.toString()}`)
    }
  }

  const handleCancel = () => {
    navigate(-1)
  }

  const handleToggleMapType = () => {
    const next = mapType === 'roadmap' ? 'satellite' : 'roadmap'
    setMapType(next)
    mapInstanceRef.current?.setMapTypeId(next)
  }

  const handleCenterOnMe = () => {
    if (!navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        }
        const marker = markerRef.current
        if (marker) {
          const map = marker.getMap() as google.maps.Map
          map.setCenter(pos)
          map.setZoom(16)
          marker.setPosition(pos)
          setSelectedPos(pos)
        }
      },
      (err) => alert(`Could not get location: ${err.message}`),
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }

  return (
    <div className="location-picker-page">
      {/* Header */}
      <header className="location-picker-header">
        <button className="location-picker-cancel" onClick={handleCancel}>
          ← {t('common:cancel')}
        </button>
        <h1>{t('pickLocation')}</h1>
        <button
          className="location-picker-confirm"
          onClick={handleConfirm}
          disabled={!selectedPos}
        >
          ✓ {t('common:confirm')}
        </button>
      </header>

      {/* Instructions */}
      <div className="location-picker-hint">
        {t('locationPickerHint')}
      </div>

      {/* Map */}
      {loading && (
        <div className="location-picker-loading">
          <div className="location-picker-spinner" />
          <p>{t('loadingMap')}</p>
        </div>
      )}

      {error && (
        <div className="location-picker-error">
          <p>⚠️ {error}</p>
          <button onClick={handleCancel}>{t('common:goBack')}</button>
        </div>
      )}

      {!error && <div ref={mapRef} className="location-picker-map" />}

      {/* Bottom bar */}
      {selectedPos && !loading && !error && (
        <div className="location-picker-bottom">
          <div className="location-picker-coords">
            📍 {selectedPos.lat.toFixed(6)}, {selectedPos.lng.toFixed(6)}
          </div>
          <div className="location-picker-bottom-actions">
            <button className="location-picker-map-type-btn" onClick={handleToggleMapType}>
              {mapType === 'roadmap' ? '🛰 Satellite' : '🗺 Map'}
            </button>
            <button className="location-picker-gps-btn" onClick={handleCenterOnMe}>
              {t('myLocation')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
